import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardActionArea,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    Add,
    ArrowBack,
    ContentCopy,
    DeleteOutline,
    EditOutlined,
    Publish,
} from "@mui/icons-material";
import {
    cloneRubric,
    cloneRubricVersion,
    createRubric,
    createRubricVersion,
    deleteRubric,
    deleteRubricVersion,
    fetchRubrics,
    fetchRubricVersions,
    fetchFullRubricVersion,
    listCodeWorkflows,
    fetchCodeWorkflow,
    listScoringConfigs,
    publishRubricVersion,
    updateRubric,
    updateRubricVersion,
} from "../../../utils/api";
import type { ScoringConfigSummary } from "../../../utils/api";
import { policiesContext } from "../../../utils/common";
import type { Rubric, RubricVersion, CodeWorkflow, FullRubricVersion } from "../../../types/rubric";
import RubricManagementList, { Column } from "../../molecules/RubricManagementList";
import { useListState } from "../../../hooks/useListState";
import { ResolverConfigurationSelect } from "./ResolverConfigurationSelect";
import { ConfirmationDialog } from "./ConfirmationDialog";

const versionDefaults = {
    majorVersion: "1",
    minorVersion: "0",
    description: "",
    procedureWorkflowId: null as number | null | undefined,
    modifierWorkflowId: null as number | null | undefined,
    diagnosisWorkflowId: null as number | null | undefined,
    caseWorkflowId: null as number | null | undefined,
    spanWorkflowId: null as number | null | undefined,
};

const computeNextVersionDefaults = (
    existingVersions: RubricVersion[]
): RubricVersionFormValues => {
    if (!existingVersions || existingVersions.length === 0) {
        return versionDefaults;
    }
    const highest = existingVersions.reduce(
        (acc, version) => {
            const major = parseInt(version.majorVersion ?? "0", 10) || 0;
            const minor = parseInt(version.minorVersion ?? "0", 10) || 0;
            if (major > acc.major || (major === acc.major && minor > acc.minor)) {
                return { major, minor };
            }
            return acc;
        },
        { major: 0, minor: 0 }
    );
    return {
        ...versionDefaults,
        majorVersion: String(highest.major),
        minorVersion: String(highest.minor + 1),
    };
};

type ViewMode = "list" | "rubricDetail" | "versionDetail";

interface RubricsPanelProps {
    onPathChange?: (path: string) => void;
}

type RubricFormValues = {
    name: string;
    description?: string;
    active?: boolean;
};

type RubricVersionFormValues = {
    majorVersion: string;
    minorVersion: string;
    description?: string;
    effectiveDate?: string;
    expirationDate?: string;
    active?: boolean;
    procedureWorkflowId?: number | null;
    modifierWorkflowId?: number | null;
    diagnosisWorkflowId?: number | null;
    caseWorkflowId?: number | null;
    spanWorkflowId?: number | null;
    activeScoringConfigId?: number | null;
};

// Column configuration for the rubrics list
const rubricColumns: Column<Rubric>[] = [
    {
        field: "name",
        label: "Name",
        sortable: true,
        render: (item) => (
            <Box>
                <Typography variant="body1" noWrap>
                    {item.name}
                </Typography>
                {item.description && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {item.description}
                    </Typography>
                )}
            </Box>
        ),
    },
    {
        field: "description",
        label: "Description",
        sortable: true,
        render: (item) => (
            <Typography variant="body2" color="text.secondary" noWrap>
                {item.description || "—"}
            </Typography>
        ),
    },
];

const RubricsPanel: React.FC<RubricsPanelProps> = ({ onPathChange }) => {
    const { userContext } = useContext(policiesContext);
    const [viewMode, setViewMode] = useState<ViewMode>("list");
    const [rubrics, setRubrics] = useState<Rubric[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Use URL-based list state for pagination, sorting, and search (client-side filtering)
    const listState = useListState({ defaultSortBy: "name", defaultSortDirection: "asc" });

    const [selectedRubric, setSelectedRubric] = useState<Rubric | null>(null);
    const [rubricFormValues, setRubricFormValues] = useState<RubricFormValues>({
        name: "",
        description: "",
        active: true,
    });
    const [rubricSaving, setRubricSaving] = useState(false);
    const [rubricError, setRubricError] = useState<string | null>(null);

    const [versions, setVersions] = useState<RubricVersion[]>([]);
    const [versionsLoading, setVersionsLoading] = useState(false);
    const [versionsError, setVersionsError] = useState<string | null>(null);

    const [editingVersion, setEditingVersion] = useState<RubricVersion | null>(null);
    const [versionFormValues, setVersionFormValues] = useState<RubricVersionFormValues>(versionDefaults);
    const [versionSubmitting, setVersionSubmitting] = useState(false);
    const [publishingVersionId, setPublishingVersionId] = useState<number | null>(null);
    const [versionError, setVersionError] = useState<string | null>(null);

    const [workflows, setWorkflows] = useState<CodeWorkflow[]>([]);
    const [workflowsLoading, setWorkflowsLoading] = useState(false);
    const [workflowError, setWorkflowError] = useState<string | null>(null);

    const [scoringConfigs, setScoringConfigs] = useState<ScoringConfigSummary[]>([]);
    const [scoringConfigsLoading, setScoringConfigsLoading] = useState(false);

    const [cloneRubricDialog, setCloneRubricDialog] = useState({
        open: false,
        source: null as Rubric | null,
        name: "",
        description: "",
        active: true,
        submitting: false,
        error: "",
    });

    const [cloneVersionDialog, setCloneVersionDialog] = useState<{
        open: boolean;
        source: RubricVersion | null;
        targetRubricId: number | null;
        majorVersion: string;
        minorVersion: string;
        description: string;
        effectiveDate: string;
        expirationDate: string;
        active: boolean;
        submitting: boolean;
        error: string;
    }>({
        open: false,
        source: null,
        targetRubricId: null,
        majorVersion: "",
        minorVersion: "",
        description: "",
        effectiveDate: "",
        expirationDate: "",
        active: true,
        submitting: false,
        error: "",
    });

    const [fullVersionStructure, setFullVersionStructure] = useState<FullRubricVersion | null>(null);
    const [fullVersionStructureLoading, setFullVersionStructureLoading] = useState(false);
    const [fullVersionStructureError, setFullVersionStructureError] = useState<string | null>(null);
    const [deleteRubricConfirmation, setDeleteRubricConfirmation] = useState<Rubric | null>(null);
    const [deleteVersionConfirmation, setDeleteVersionConfirmation] = useState<RubricVersion | null>(null);

    // Client-side filtering since rubrics typically have smaller datasets
    const filteredRubrics = useMemo(() => {
        let result = [...rubrics];
        
        // Apply search filter
        if (listState.search.trim()) {
            const normalized = listState.search.trim().toLowerCase();
            result = result.filter((rubric) =>
                [rubric.name, rubric.description]
                    .filter(Boolean)
                    .some((value) => value?.toLowerCase().includes(normalized))
            );
        }
        
        // Apply sorting
        result.sort((a, b) => {
            let aValue: string | number | undefined;
            let bValue: string | number | undefined;
            
            switch (listState.sortBy) {
                case "name":
                    aValue = a.name?.toLowerCase() ?? "";
                    bValue = b.name?.toLowerCase() ?? "";
                    break;
                case "description":
                    aValue = a.description?.toLowerCase() ?? "";
                    bValue = b.description?.toLowerCase() ?? "";
                    break;
                default:
                    aValue = a.name?.toLowerCase() ?? "";
                    bValue = b.name?.toLowerCase() ?? "";
            }
            
            if (aValue < bValue) return listState.sortDirection === "asc" ? -1 : 1;
            if (aValue > bValue) return listState.sortDirection === "asc" ? 1 : -1;
            return 0;
        });
        
        return result;
    }, [rubrics, listState.search, listState.sortBy, listState.sortDirection]);

    const paginatedRubrics = useMemo(() => {
        const start = listState.page * listState.pageSize;
        return filteredRubrics.slice(start, start + listState.pageSize);
    }, [filteredRubrics, listState.page, listState.pageSize]);

    // Reset page when filtered results change
    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(filteredRubrics.length / listState.pageSize) - 1);
        if (listState.page > maxPage) {
            listState.setPage(0);
        }
    }, [filteredRubrics.length, listState.pageSize, listState.page, listState.setPage]);

    const procedureOptions = useMemo(
        () =>
            workflows.filter((workflow) => {
                const type = workflow.codeType?.toUpperCase?.();
                return type === "PROCEDURE" || type === "CPT";
            }),
        [workflows]
    );

    const modifierOptions = useMemo(
        () =>
            workflows.filter((workflow) => {
                const type = workflow.codeType?.toUpperCase?.();
                return type === "MODIFIER";
            }),
        [workflows]
    );

    const diagnosisOptions = useMemo(
        () =>
            workflows.filter((workflow) => {
                const type = workflow.codeType?.toUpperCase?.();
                return type === "DIAGNOSIS" || type === "ICD";
            }),
        [workflows]
    );

    const caseOptions = useMemo(
        () =>
            workflows.filter((workflow) => workflow.codeType?.toUpperCase?.() === "CASE"),
        [workflows]
    );

    const spanOptions = useMemo(
        () =>
            workflows.filter((workflow) => workflow.codeType?.toUpperCase?.() === "SPAN"),
        [workflows]
    );

    const loadRubrics = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchRubrics();
            setRubrics(response ?? []);
        } catch (err) {
            console.error("Failed to fetch rubrics", err);
            setError(err instanceof Error ? err.message : "Unable to load workflow collections");
        } finally {
            setLoading(false);
        }
    }, []);

    const loadRubricVersions = useCallback(async (rubricId: number) => {
        setVersionsLoading(true);
        setVersionsError(null);
        try {
            const response = await fetchRubricVersions(rubricId);
            setVersions(response ?? []);
        } catch (err) {
            console.error("Failed to load rubric versions", err);
            setVersionsError(err instanceof Error ? err.message : "Unable to load workflow collection versions");
        } finally {
            setVersionsLoading(false);
        }
    }, []);

    const loadAvailableWorkflows = useCallback(async () => {
        setWorkflowsLoading(true);
        setWorkflowError(null);
        try {
            const response = await listCodeWorkflows({ active: true });
            setWorkflows(response ?? []);
        } catch (err) {
            console.error("Failed to load code workflows", err);
            setWorkflowError(err instanceof Error ? err.message : "Unable to load code workflows");
        } finally {
            setWorkflowsLoading(false);
        }
    }, []);

    const loadScoringConfigs = useCallback(async () => {
        setScoringConfigsLoading(true);
        try {
            const configs = await listScoringConfigs();
            setScoringConfigs(configs ?? []);
        } catch (err) {
            console.error("Failed to load scoring configs", err);
            setScoringConfigs([]);
        } finally {
            setScoringConfigsLoading(false);
        }
    }, []);

    const getWorkflowLabel = useCallback(
        (id?: number | null) => {
            if (!id) {
                return "";
            }
            const workflow = workflows.find((wf) => wf.id === id);
            return workflow?.description || workflow?.name || `Workflow ${id}`;
        },
        [workflows]
    );

    useEffect(() => {
        loadRubrics();
        loadAvailableWorkflows();
        loadScoringConfigs();
    }, [loadRubrics, loadAvailableWorkflows, loadScoringConfigs]);

    useEffect(() => {
        if (viewMode !== "versionDetail") {
            setFullVersionStructure(null);
            setFullVersionStructureError(null);
            setFullVersionStructureLoading(false);
            return;
        }
        if (!editingVersion?.id) {
            setFullVersionStructure(null);
            setFullVersionStructureError(null);
        }
    }, [viewMode, editingVersion?.id]);

    const handleSelectRubric = async (rubric: Rubric) => {
        setSelectedRubric(rubric);
        setRubricFormValues({
            name: rubric.name,
            description: rubric.description ?? "",
            active: rubric.active ?? true,
        });
        setViewMode("rubricDetail");
        await Promise.allSettled([loadRubricVersions(rubric.id), loadAvailableWorkflows()]);
    };

    const handleStartCreateRubric = () => {
        setSelectedRubric(null);
        setRubricFormValues({ name: "", description: "", active: true });
        setVersions([]);
        setViewMode("rubricDetail");
    };

    const handleDeleteRubric = (rubric: Rubric) => {
        setDeleteRubricConfirmation(rubric);
    };

    const confirmDeleteRubric = async () => {
        if (!deleteRubricConfirmation) {
            return;
        }
        const rubric = deleteRubricConfirmation;
        setDeleteRubricConfirmation(null);
        try {
            await deleteRubric(rubric.id);
            await loadRubrics();
            if (selectedRubric?.id === rubric.id) {
                handleBackToList();
            }
        } catch (err) {
            console.error("Failed to delete rubric", err);
            setError(err instanceof Error ? err.message : "Unable to delete workflow collection");
        }
    };

    const handleBackToList = () => {
        setViewMode("list");
        setSelectedRubric(null);
        setRubricFormValues({ name: "", description: "", active: true });
        setEditingVersion(null);
        setVersionFormValues(versionDefaults);
        setVersionError(null);
        setRubricError(null);
    };

    const handleBackToRubric = () => {
        setViewMode("rubricDetail");
        setEditingVersion(null);
        setVersionFormValues(versionDefaults);
        setVersionError(null);
    };

    const handleRubricSave = async () => {
        if (!rubricFormValues.name.trim()) {
            setRubricError("Name is required");
            return;
        }
        setRubricSaving(true);
        setRubricError(null);
        try {
            if (selectedRubric) {
                const updated = await updateRubric(selectedRubric.id, {
                    name: rubricFormValues.name.trim(),
                    description: rubricFormValues.description?.trim(),
                    active: rubricFormValues.active,
                });
                setSelectedRubric(updated);
                setRubrics((prev) => prev.map((rubric) => (rubric.id === updated.id ? updated : rubric)));
                setRubricFormValues({
                    name: updated.name,
                    description: updated.description ?? "",
                    active: updated.active,
                });
            } else {
                const created = await createRubric({
                    name: rubricFormValues.name.trim(),
                    description: rubricFormValues.description?.trim(),
                });
                setRubrics((prev) => [...prev, created]);
                setSelectedRubric(created);
                await Promise.allSettled([
                    loadRubricVersions(created.id),
                    loadAvailableWorkflows(),
                ]);
            }
        } catch (err) {
            console.error("Unable to save rubric", err);
            setRubricError(err instanceof Error ? err.message : "Unable to save workflow collection");
        } finally {
            setRubricSaving(false);
        }
    };

    const handleOpenVersionForm = async (version?: RubricVersion) => {
        if (!selectedRubric) {
            return;
        }
        if (!version && versionsLoading) {
            return;
        }
        if (version) {
            setEditingVersion(version);
            setVersionFormValues({
                majorVersion: version.majorVersion,
                minorVersion: version.minorVersion,
                description: version.taskConfiguration?.description ?? "",
                effectiveDate: version.effectiveDate ?? undefined,
                expirationDate: version.expirationDate ?? undefined,
                active: version.active ?? true,
                procedureWorkflowId: version.procedureWorkflowId ?? null,
                modifierWorkflowId: version.modifierWorkflowId ?? null,
                diagnosisWorkflowId: version.diagnosisWorkflowId ?? null,
                caseWorkflowId: version.caseWorkflowId ?? null,
                spanWorkflowId: version.spanWorkflowId ?? null,
                activeScoringConfigId: version.activeScoringConfigId ?? null,
            });

            // If the version references workflows not in the active list, fetch and add them
            const workflowIds = [
                version.procedureWorkflowId,
                version.modifierWorkflowId,
                version.diagnosisWorkflowId,
                version.caseWorkflowId,
                version.spanWorkflowId,
            ].filter((id): id is number => id != null);
            const missingWorkflowIds = workflowIds.filter(
                (id) => !workflows.some((wf) => wf.id === id)
            );
            if (missingWorkflowIds.length > 0) {
                const inactiveWorkflows = await Promise.all(
                    missingWorkflowIds.map((id) =>
                        fetchCodeWorkflow(id).catch((err) => {
                            console.warn(`Could not fetch inactive workflow ${id}`, err);
                            return null;
                        })
                    )
                );
                const validWorkflows = inactiveWorkflows.filter(
                    (wf): wf is CodeWorkflow => wf !== null
                );
                if (validWorkflows.length > 0) {
                    setWorkflows((prev) => [...prev, ...validWorkflows]);
                }
            }
        } else {
            setEditingVersion(null);
            setVersionFormValues(computeNextVersionDefaults(versions));
        }
        setVersionError(null);
        setViewMode("versionDetail");
    };

    const handleVersionSave = async () => {
        if (!selectedRubric) {
            return;
        }
        if (!editingVersion && (!versionFormValues.majorVersion.trim() || !versionFormValues.minorVersion.trim())) {
            setVersionError("Major and minor versions are required");
            return;
        }
        setVersionSubmitting(true);
        setVersionError(null);
        try {
            const payload = {
                description: versionFormValues.description,
                effectiveDate: versionFormValues.effectiveDate,
                expirationDate: versionFormValues.expirationDate,
                procedureWorkflowId: versionFormValues.procedureWorkflowId,
                modifierWorkflowId: versionFormValues.modifierWorkflowId,
                diagnosisWorkflowId: versionFormValues.diagnosisWorkflowId,
                caseWorkflowId: versionFormValues.caseWorkflowId,
                spanWorkflowId: versionFormValues.spanWorkflowId,
                activeScoringConfigId: versionFormValues.activeScoringConfigId ?? null,
            };

            if (editingVersion) {
                await updateRubricVersion(editingVersion.id, {
                    ...payload,
                    active: versionFormValues.active,
                });
            } else {
                await createRubricVersion(selectedRubric.id, {
                    ...payload,
                    majorVersion: versionFormValues.majorVersion.trim(),
                    minorVersion: versionFormValues.minorVersion.trim(),
                });
            }
            await loadRubricVersions(selectedRubric.id);
            handleBackToRubric();
        } catch (err) {
            console.error("Unable to save version", err);
            setVersionError(err instanceof Error ? err.message : "Unable to save version");
        } finally {
            setVersionSubmitting(false);
        }
    };

    const handleDeleteVersion = (version: RubricVersion) => {
        setDeleteVersionConfirmation(version);
    };

    const confirmDeleteVersion = async () => {
        if (!deleteVersionConfirmation) {
            return;
        }
        const version = deleteVersionConfirmation;
        setDeleteVersionConfirmation(null);
        try {
            await deleteRubricVersion(version.id);
            if (selectedRubric) {
                await loadRubricVersions(selectedRubric.id);
            }
        } catch (err) {
            console.error("Failed to delete version", err);
            setVersionsError(err instanceof Error ? err.message : "Unable to delete workflow collection version");
        }
    };

    const handleToggleVersionActive = async (version: RubricVersion, active: boolean) => {
        try {
            await updateRubricVersion(version.id, { active });
            if (selectedRubric) {
                await loadRubricVersions(selectedRubric.id);
            }
        } catch (err) {
            console.error("Failed to update version", err);
            setVersionsError(err instanceof Error ? err.message : "Unable to update workflow collection version");
        }
    };

    const handlePublishVersion = async (version: RubricVersion) => {
        if (!selectedRubric) {
            return;
        }
        setPublishingVersionId(version.id);
        setVersionsError(null);
        try {
            await publishRubricVersion(version.id);
            await loadRubricVersions(selectedRubric.id);
        } catch (err) {
            console.error("Failed to publish version", err);
            setVersionsError(err instanceof Error ? err.message : "Unable to publish workflow collection version");
        } finally {
            setPublishingVersionId(null);
        }
    };

    const handleToggleRubricActive = async (rubric: Rubric, active: boolean) => {
        if (!rubric.id) {
            return;
        }
        try {
            await updateRubric(rubric.id, {
                name: rubric.name,
                description: rubric.description,
                active,
            });
            await loadRubrics();
        } catch (err) {
            console.error("Failed to update rubric", err);
            setError(err instanceof Error ? err.message : "Unable to update workflow collection");
        }
    };

    const handleOpenCloneRubric = (rubric: Rubric) => {
        setCloneRubricDialog({
            open: true,
            source: rubric,
            name: `${rubric.name} Copy`,
            description: rubric.description ?? "",
            active: Boolean(rubric.active),
            submitting: false,
            error: "",
        });
    };

    const handleCloseCloneRubricDialog = () => {
        setCloneRubricDialog((prev) => ({
            ...prev,
            open: false,
            source: null,
            submitting: false,
            error: "",
        }));
    };

    const handleCloneRubricSubmit = async () => {
        if (!cloneRubricDialog.source) {
            handleCloseCloneRubricDialog();
            return;
        }
        if (!cloneRubricDialog.name.trim()) {
            setCloneRubricDialog((prev) => ({ ...prev, error: "Name is required." }));
            return;
        }
        setCloneRubricDialog((prev) => ({ ...prev, submitting: true, error: "" }));
        try {
            await cloneRubric(cloneRubricDialog.source.id, {
                name: cloneRubricDialog.name.trim(),
                description: cloneRubricDialog.description?.trim(),
                active: cloneRubricDialog.active,
            });
            await loadRubrics();
            handleCloseCloneRubricDialog();
        } catch (err) {
            console.error("Failed to clone rubric", err);
            setCloneRubricDialog((prev) => ({
                ...prev,
                submitting: false,
                error: err instanceof Error ? err.message : "Unable to clone workflow collection",
            }));
        }
    };

    const handleOpenCloneVersionDialog = (version: RubricVersion) => {
        setCloneVersionDialog({
            open: true,
            source: version,
            targetRubricId: selectedRubric?.id ?? version.rubricId,
            majorVersion: version.majorVersion,
            minorVersion: version.minorVersion,
            description: version.taskConfiguration?.description ?? "",
            effectiveDate: version.effectiveDate ?? "",
            expirationDate: version.expirationDate ?? "",
            active: Boolean(version.active),
            submitting: false,
            error: "",
        });
    };

    const handleCloseCloneVersionDialog = () => {
        setCloneVersionDialog((prev) => ({
            ...prev,
            open: false,
            submitting: false,
            error: "",
        }));
    };

    const handleCloneVersionSubmit = async () => {
        if (!cloneVersionDialog.source) {
            handleCloseCloneVersionDialog();
            return;
        }
        if (!cloneVersionDialog.majorVersion.trim() || !cloneVersionDialog.minorVersion.trim()) {
            setCloneVersionDialog((prev) => ({
                ...prev,
                error: "Major and minor versions are required.",
            }));
            return;
        }
        setCloneVersionDialog((prev) => ({ ...prev, submitting: true, error: "" }));
        try {
            await cloneRubricVersion(cloneVersionDialog.source.id, {
                targetRubricId: cloneVersionDialog.targetRubricId ?? undefined,
                majorVersion: cloneVersionDialog.majorVersion.trim(),
                minorVersion: cloneVersionDialog.minorVersion.trim(),
                description: cloneVersionDialog.description?.trim(),
                effectiveDate: cloneVersionDialog.effectiveDate || undefined,
                expirationDate: cloneVersionDialog.expirationDate || undefined,
                active: cloneVersionDialog.active,
            });
            const targetId = cloneVersionDialog.targetRubricId ?? selectedRubric?.id ?? null;
            if (targetId) {
                await loadRubricVersions(targetId);
            }
            handleCloseCloneVersionDialog();
        } catch (err) {
            console.error("Failed to clone rubric version", err);
            setCloneVersionDialog((prev) => ({
                ...prev,
                submitting: false,
                error: err instanceof Error ? err.message : "Unable to clone workflow collection version",
            }));
        }
    };

    const handleLoadFullVersionStructure = async () => {
        if (!editingVersion?.id) {
            setFullVersionStructureError("Select a version to load its structure.");
            return;
        }
        setFullVersionStructureLoading(true);
        setFullVersionStructureError(null);
        try {
            const structure = await fetchFullRubricVersion(editingVersion.id);
            setFullVersionStructure(structure);
        } catch (err) {
            console.error("Failed to load full rubric structure", err);
            setFullVersionStructureError(
                err instanceof Error ? err.message : "Unable to load workflow collection structure"
            );
        } finally {
            setFullVersionStructureLoading(false);
        }
    };

    const handleClearFullVersionStructure = () => {
        setFullVersionStructure(null);
        setFullVersionStructureError(null);
    };

    const handleCopyFullVersionStructure = async () => {
        if (!fullVersionStructure) {
            return;
        }
        try {
            const jsonString = JSON.stringify(fullVersionStructure, null, 2);
            await navigator.clipboard.writeText(jsonString);
        } catch (err) {
            console.error("Failed to copy to clipboard", err);
            setFullVersionStructureError("Failed to copy to clipboard");
        }
    };

    const pathSuffix = useMemo(() => {
        if (viewMode === "list") {
            return "Workflow Collections";
        }
        if (viewMode === "rubricDetail") {
            return selectedRubric ? "Update Workflow Collection" : "Create Workflow Collection";
        }
        const versionLabel = editingVersion
            ? `Update Workflow Collection / Version ${editingVersion.majorVersion}.${editingVersion.minorVersion}`
            : "Update Workflow Collection / Add New Version";
        return versionLabel;
    }, [viewMode, selectedRubric, editingVersion]);

    useEffect(() => {
        if (onPathChange) {
            onPathChange(pathSuffix);
        }
    }, [onPathChange, pathSuffix]);

    const renderRubricActions = (rubric: Rubric) => (
        <>
            <Tooltip title="Clone">
                <IconButton
                    aria-label="Clone Workflow Collection"
                    onClick={(event) => {
                        event.stopPropagation();
                        handleOpenCloneRubric(rubric);
                    }}
                    sx={{ color: "#7498A6" }}
                >
                    <ContentCopy fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
                <IconButton
                    aria-label="Edit Workflow Collection"
                    onClick={(event) => {
                        event.stopPropagation();
                        handleSelectRubric(rubric);
                    }}
                    sx={{ color: "#7498A6" }}
                >
                    <EditOutlined fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
                <IconButton
                    aria-label={`Delete workflow collection ${rubric.name}`}
                    onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteRubric(rubric);
                    }}
                    sx={{ color: "#7498A6" }}
                >
                    <DeleteOutline fontSize="small" />
                </IconButton>
            </Tooltip>
        </>
    );

    const renderRubricList = () => (
        <RubricManagementList<Rubric>
            items={paginatedRubrics}
            totalCount={filteredRubrics.length}
            loading={loading}
            error={error}
            listState={listState}
            columns={rubricColumns}
            getItemKey={(item) => item.id}
            onItemClick={handleSelectRubric}
            onAddClick={handleStartCreateRubric}
            searchPlaceholder="Search workflow collections by name or description..."
            addButtonLabel="Add Workflow Collection"
            itemLabel="workflow collections"
            emptyMessage="No workflow collections found."
            renderActions={renderRubricActions}
            onToggleActive={handleToggleRubricActive}
            isItemActive={(item) => Boolean(item.active)}
        />
    );
    const renderVersionRow = (version: RubricVersion) => (
        <Paper
            key={version.id}
            variant="outlined"
            sx={{
                cursor: "pointer",
                px: 2,
                py: 1.5,
                backgroundColor: "#F6FBFF",
                borderColor: "#BBDCFB",
                borderRadius: "3px",
            }}
            onClick={() => handleOpenVersionForm(version)}
        >
            <Stack direction="row" spacing={2} alignItems="center" width="100%">
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body1" noWrap>
                        Version {version.majorVersion}.{version.minorVersion}
                    </Typography>
                    {version.taskConfiguration?.description && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {version.taskConfiguration.description}
                        </Typography>
                    )}
                    {(version.effectiveDate || version.expirationDate) && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {version.effectiveDate ? `Effective: ${version.effectiveDate}` : ""}
                            {version.effectiveDate && version.expirationDate ? " · " : ""}
                            {version.expirationDate ? `Expires: ${version.expirationDate}` : ""}
                        </Typography>
                    )}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: "auto" }}>
                    <Box
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "999px",
                            backgroundColor: version.status === "PUBLISHED" ? "#E8F5E9" : "#FFF3E0",
                            color: "#4A5568",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                        }}
                    >
                        {version.status === "PUBLISHED" ? "Published" : "Draft"}
                    </Box>
                    {version.caseWorkflowId && (
                        <Box
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                borderRadius: "999px",
                                backgroundColor: "#E3F2FD",
                                color: "#1E3A8A",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                            }}
                        >
                            Case: {getWorkflowLabel(version.caseWorkflowId)}
                        </Box>
                    )}
                    {version.status === "DRAFT" && (
                        <Tooltip title="Publish Version">
                            <IconButton
                                size="small"
                                aria-label={`Publish Version ${version.majorVersion}.${version.minorVersion}`}
                                disabled={publishingVersionId === version.id}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handlePublishVersion(version);
                                }}
                                sx={{ color: "#7498A6" }}
                            >
                                <Publish fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Clone Version">
                        <IconButton
                            size="small"
                            onClick={(event) => {
                                event.stopPropagation();
                                handleOpenCloneVersionDialog(version);
                            }}
                            sx={{ color: "#7498A6" }}
                        >
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Version">
                        <IconButton
                            size="small"
                            onClick={(event) => {
                                event.stopPropagation();
                                handleOpenVersionForm(version);
                            }}
                            sx={{ color: "#7498A6" }}
                        >
                            <EditOutlined fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>
        </Paper>
    );

    const renderVersionList = () => (
        <Box>
            <Stack direction="row" justifyContent="flex-end" alignItems="center" mb={2}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => handleOpenVersionForm()}
                    disabled={!selectedRubric || versionsLoading}
                >
                    Add Version
                </Button>
            </Stack>
            {versionsError && <Alert severity="warning" sx={{ mb: 2 }}>{versionsError}</Alert>}
            {versionsLoading ? (
                <Stack alignItems="center" py={4}>
                    <CircularProgress size={24} />
                </Stack>
            ) : versions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No versions yet. Add a version to get started.
                </Typography>
            ) : (
                <Stack spacing={1.5}>{versions.map(renderVersionRow)}</Stack>
            )}
        </Box>
    );

    const renderRubricDetail = () => {
        const isExisting = Boolean(selectedRubric);
        return (
            <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleBackToList}>
                        Back to Workflow Collections
                    </Button>
                    <Stack direction="row" spacing={1}>
                        <Button onClick={handleBackToList}>Cancel</Button>
                        <Button variant="contained" onClick={handleRubricSave} disabled={rubricSaving}>
                            {rubricSaving ? "Saving..." : isExisting ? "Update" : "Create"}
                        </Button>
                    </Stack>
                </Stack>
                <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {rubricError && <Alert severity="error">{rubricError}</Alert>}
                    <TextField
                        label="Name"
                        value={rubricFormValues.name}
                        onChange={(event) =>
                            setRubricFormValues((prev) => ({ ...prev, name: event.target.value }))
                        }
                        required
                        fullWidth
                    />
                    <TextField
                        label="Description"
                        value={rubricFormValues.description ?? ""}
                        onChange={(event) =>
                            setRubricFormValues((prev) => ({ ...prev, description: event.target.value }))
                        }
                        fullWidth
                        multiline
                        minRows={3}
                    />
                </Box>
                <Divider sx={{ my: 3 }} />
                {isExisting ? renderVersionList() : (
                    <Alert severity="info">
                        Save the workflow collection before configuring versions.
                    </Alert>
                )}
            </Box>
        );
    };

    const renderVersionDetail = () => (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleBackToRubric}>
                    Back to Workflow Collection
                </Button>
                <Stack direction="row" spacing={1}>
                    <Button onClick={handleBackToRubric}>Cancel</Button>
                    <Button variant="contained" onClick={handleVersionSave} disabled={versionSubmitting}>
                        {versionSubmitting ? "Saving..." : editingVersion ? "Update Version" : "Add Version"}
                    </Button>
                </Stack>
            </Stack>
            <Typography variant="h5" gutterBottom>
                {editingVersion
                    ? `Edit Version ${editingVersion.majorVersion}.${editingVersion.minorVersion}`
                    : "Add New Version"}
            </Typography>
            {versionError && <Alert severity="error" sx={{ mb: 2 }}>{versionError}</Alert>}
            {workflowError && <Alert severity="warning" sx={{ mb: 2 }}>{workflowError}</Alert>}
            <Box sx={{ display: "grid", gap: 2 }}>
                {editingVersion ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2" color="text.secondary">
                            Version
                        </Typography>
                        <Chip
                            label={`${editingVersion.majorVersion}.${editingVersion.minorVersion}`}
                            variant="outlined"
                        />
                    </Stack>
                ) : (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <TextField
                            label="Major Version"
                            value={versionFormValues.majorVersion}
                            onChange={(event) =>
                                setVersionFormValues((prev) => ({
                                    ...prev,
                                    majorVersion: event.target.value.trim(),
                                }))
                            }
                            required
                            type="number"
                            fullWidth
                        />
                        <TextField
                            label="Minor Version"
                            value={versionFormValues.minorVersion}
                            onChange={(event) =>
                                setVersionFormValues((prev) => ({
                                    ...prev,
                                    minorVersion: event.target.value.trim(),
                                }))
                            }
                            required
                            type="number"
                            fullWidth
                        />
                    </Stack>
                )}
                <TextField
                    label="Description"
                    value={versionFormValues.description ?? ""}
                    onChange={(event) =>
                        setVersionFormValues((prev) => ({ ...prev, description: event.target.value }))
                    }
                    fullWidth
                    multiline
                    minRows={3}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                        label="Effective Date"
                        type="date"
                        value={versionFormValues.effectiveDate ?? ""}
                        onChange={(event) =>
                            setVersionFormValues((prev) => ({ ...prev, effectiveDate: event.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                    <TextField
                        label="Expiration Date"
                        type="date"
                        value={versionFormValues.expirationDate ?? ""}
                        onChange={(event) =>
                            setVersionFormValues((prev) => ({ ...prev, expirationDate: event.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1">Code Workflows</Typography>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <FormControl fullWidth disabled={workflowsLoading}>
                        <InputLabel id="procedure-workflow-label">Procedure Workflow</InputLabel>
                        <Select
                            labelId="procedure-workflow-label"
                            label="Procedure Workflow"
                            value={
                                versionFormValues.procedureWorkflowId !== null &&
                                versionFormValues.procedureWorkflowId !== undefined
                                    ? String(versionFormValues.procedureWorkflowId)
                                    : ""
                            }
                            onChange={(event) =>
                                setVersionFormValues((prev) => ({
                                    ...prev,
                                    procedureWorkflowId: event.target.value ? Number(event.target.value) : null,
                                }))
                            }
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {procedureOptions.map((workflow) => (
                                <MenuItem
                                    key={workflow.id}
                                    value={String(workflow.id)}
                                    disabled={workflow.active === false}
                                >
                                    {workflow.name || workflow.description || workflow.id}
                                    {workflow.active === false && (
                                        <Chip label="Inactive" size="small" color="warning" sx={{ ml: 1 }} />
                                    )}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth disabled={workflowsLoading}>
                        <InputLabel id="modifier-workflow-label">Modifier Workflow</InputLabel>
                        <Select
                            labelId="modifier-workflow-label"
                            label="Modifier Workflow"
                            value={
                                versionFormValues.modifierWorkflowId !== null &&
                                versionFormValues.modifierWorkflowId !== undefined
                                    ? String(versionFormValues.modifierWorkflowId)
                                    : ""
                            }
                            onChange={(event) =>
                                setVersionFormValues((prev) => ({
                                    ...prev,
                                    modifierWorkflowId: event.target.value ? Number(event.target.value) : null,
                                }))
                            }
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {modifierOptions.map((workflow) => (
                                <MenuItem
                                    key={workflow.id}
                                    value={String(workflow.id)}
                                    disabled={workflow.active === false}
                                >
                                    {workflow.name || workflow.description || workflow.id}
                                    {workflow.active === false && (
                                        <Chip label="Inactive" size="small" color="warning" sx={{ ml: 1 }} />
                                    )}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth disabled={workflowsLoading}>
                        <InputLabel id="diagnosis-workflow-label">Diagnosis Workflow</InputLabel>
                        <Select
                            labelId="diagnosis-workflow-label"
                            label="Diagnosis Workflow"
                            value={
                                versionFormValues.diagnosisWorkflowId !== null &&
                                versionFormValues.diagnosisWorkflowId !== undefined
                                    ? String(versionFormValues.diagnosisWorkflowId)
                                    : ""
                            }
                            onChange={(event) =>
                                setVersionFormValues((prev) => ({
                                    ...prev,
                                    diagnosisWorkflowId: event.target.value ? Number(event.target.value) : null,
                                }))
                            }
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {diagnosisOptions.map((workflow) => (
                                <MenuItem
                                    key={workflow.id}
                                    value={String(workflow.id)}
                                    disabled={workflow.active === false}
                                >
                                    {workflow.name || workflow.description || workflow.id}
                                    {workflow.active === false && (
                                        <Chip label="Inactive" size="small" color="warning" sx={{ ml: 1 }} />
                                    )}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
                <FormControl fullWidth disabled={workflowsLoading}>
                    <InputLabel id="case-workflow-label">Case Workflow</InputLabel>
                    <Select
                        labelId="case-workflow-label"
                        label="Case Workflow"
                        value={
                            versionFormValues.caseWorkflowId !== null &&
                            versionFormValues.caseWorkflowId !== undefined
                                ? String(versionFormValues.caseWorkflowId)
                                : ""
                        }
                        onChange={(event) =>
                            setVersionFormValues((prev) => ({
                                ...prev,
                                caseWorkflowId: event.target.value ? Number(event.target.value) : null,
                            }))
                        }
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {caseOptions.map((workflow) => (
                            <MenuItem
                                key={workflow.id}
                                value={String(workflow.id)}
                                disabled={workflow.active === false}
                            >
                                {workflow.name || workflow.description || workflow.id}
                                {workflow.active === false && (
                                    <Chip label="Inactive" size="small" color="warning" sx={{ ml: 1 }} />
                                )}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth disabled={workflowsLoading}>
                    <InputLabel id="span-workflow-label">Span Workflow</InputLabel>
                    <Select
                        labelId="span-workflow-label"
                        label="Span Workflow"
                        value={
                            versionFormValues.spanWorkflowId !== null &&
                            versionFormValues.spanWorkflowId !== undefined
                                ? String(versionFormValues.spanWorkflowId)
                                : ""
                        }
                        onChange={(event) =>
                            setVersionFormValues((prev) => ({
                                ...prev,
                                spanWorkflowId: event.target.value ? Number(event.target.value) : null,
                            }))
                        }
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {spanOptions.map((workflow) => (
                            <MenuItem
                                key={workflow.id}
                                value={String(workflow.id)}
                                disabled={workflow.active === false}
                            >
                                {workflow.name || workflow.description || workflow.id}
                                {workflow.active === false && (
                                    <Chip label="Inactive" size="small" color="warning" sx={{ ml: 1 }} />
                                )}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <ResolverConfigurationSelect rubricVersionId={editingVersion?.id ?? null} />
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1">Scoring configuration</Typography>
                <Autocomplete<ScoringConfigSummary, false, false>
                    fullWidth
                    disabled={scoringConfigsLoading}
                    options={scoringConfigs}
                    value={scoringConfigs.find((c) => c.id === versionFormValues.activeScoringConfigId) ?? null}
                    getOptionLabel={(opt) => opt.name}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    onChange={(_, newValue) =>
                        setVersionFormValues((prev) => ({
                            ...prev,
                            activeScoringConfigId: newValue?.id ?? null,
                        }))
                    }
                    loading={scoringConfigsLoading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Scoring configuration"
                            placeholder="Search or select..."
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            <Box>
                                <Typography variant="body2">{option.name}</Typography>
                                {option.description && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {option.description}
                                    </Typography>
                                )}
                            </Box>
                        </li>
                    )}
                />
                {editingVersion?.id && (
                    <Paper
                        variant="outlined"
                        sx={{
                            mt: 2,
                            px: 2,
                            py: 1.5,
                            backgroundColor: "#F6FBFF",
                            borderColor: "#BBDCFB",
                            borderRadius: "3px",
                            flexDirection: "column",
                        }}
                    >
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.5}
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            justifyContent="space-between"
                        >
                            <Typography variant="subtitle2">Debug: Full Workflow Collection Structure</Typography>
                            <Stack direction="row" spacing={1}>
                                {fullVersionStructure && (
                                    <Button size="small" onClick={handleClearFullVersionStructure}>
                                        Clear
                                    </Button>
                                )}
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handleLoadFullVersionStructure}
                                    disabled={fullVersionStructureLoading}
                                >
                                    {fullVersionStructureLoading ? "Loading..." : "Load Structure"}
                                </Button>
                                {fullVersionStructure && (
                                    <Tooltip title="Copy to clipboard">
                                        <IconButton
                                            size="small"
                                            onClick={handleCopyFullVersionStructure}
                                            aria-label="Copy full workflow collection structure"
                                        >
                                            <ContentCopy fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>
                        </Stack>
                        {fullVersionStructureError && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                                {fullVersionStructureError}
                            </Alert>
                        )}
                        {fullVersionStructure && (
                            <Box
                                component="pre"
                                data-testid="full-rubric-structure"
                                sx={{
                                    mt: 2,
                                    mb: 0,
                                    p: 2,
                                    backgroundColor: "#0F1D2B",
                                    color: "#E0F2FF",
                                    borderRadius: 1,
                                    width: "100%",
                                    overflow: "auto",
                                    maxHeight: 320,
                                    fontSize: "0.75rem",
                                }}
                            >
                                {JSON.stringify(fullVersionStructure, null, 2)}
                            </Box>
                        )}
                    </Paper>
                )}
            </Box>
        </Box>
    );

    return (
        <Box>
            {viewMode === "list" && renderRubricList()}
            {viewMode === "rubricDetail" && renderRubricDetail()}
            {viewMode === "versionDetail" && renderVersionDetail()}
            <ConfirmationDialog
                open={Boolean(deleteRubricConfirmation)}
                title="Delete workflow collection?"
                description={
                    deleteRubricConfirmation
                        ? `Delete workflow collection "${deleteRubricConfirmation.name}"?`
                        : ""
                }
                confirmLabel="Delete"
                confirmColor="error"
                titleId="delete-workflow-collection-title"
                descriptionId="delete-workflow-collection-description"
                onCancel={() => setDeleteRubricConfirmation(null)}
                onConfirm={confirmDeleteRubric}
            />
            <ConfirmationDialog
                open={Boolean(deleteVersionConfirmation)}
                title="Delete workflow collection version?"
                description={
                    deleteVersionConfirmation
                        ? `Delete version ${deleteVersionConfirmation.majorVersion}.${deleteVersionConfirmation.minorVersion}?`
                        : ""
                }
                confirmLabel="Delete"
                confirmColor="error"
                titleId="delete-workflow-collection-version-title"
                descriptionId="delete-workflow-collection-version-description"
                onCancel={() => setDeleteVersionConfirmation(null)}
                onConfirm={confirmDeleteVersion}
            />
            <Dialog open={cloneRubricDialog.open} onClose={handleCloseCloneRubricDialog} fullWidth maxWidth="sm">
                <DialogTitle>Clone Workflow Collection</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <TextField
                            label="New Workflow Collection Name"
                            value={cloneRubricDialog.name}
                            onChange={(event) =>
                                setCloneRubricDialog((prev) => ({ ...prev, name: event.target.value }))
                            }
                            required
                            autoFocus
                        />
                        <TextField
                            label="Description"
                            value={cloneRubricDialog.description}
                            onChange={(event) =>
                                setCloneRubricDialog((prev) => ({ ...prev, description: event.target.value }))
                            }
                            multiline
                            minRows={2}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={cloneRubricDialog.active}
                                    onChange={(event) =>
                                        setCloneRubricDialog((prev) => ({ ...prev, active: event.target.checked }))
                                    }
                                />
                            }
                            label="Active"
                        />
                        {cloneRubricDialog.error && <Alert severity="error">{cloneRubricDialog.error}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCloneRubricDialog} disabled={cloneRubricDialog.submitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCloneRubricSubmit}
                        disabled={cloneRubricDialog.submitting}
                    >
                        {cloneRubricDialog.submitting ? "Cloning..." : "Clone"}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={cloneVersionDialog.open} onClose={handleCloseCloneVersionDialog} fullWidth maxWidth="sm">
                <DialogTitle>Clone Workflow Collection Version</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <FormControl fullWidth>
                            <InputLabel id="target-rubric-label">Target Workflow Collection</InputLabel>
                            <Select
                                labelId="target-rubric-label"
                                label="Target Workflow Collection"
                                value={cloneVersionDialog.targetRubricId ? String(cloneVersionDialog.targetRubricId) : ""}
                                onChange={(event) =>
                                    setCloneVersionDialog((prev) => ({
                                        ...prev,
                                        targetRubricId: event.target.value ? Number(event.target.value) : null,
                                    }))
                                }
                            >
                                {rubrics.map((rubric) => (
                                    <MenuItem key={rubric.id} value={String(rubric.id)}>
                                        {rubric.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <TextField
                                label="Major Version"
                                value={cloneVersionDialog.majorVersion}
                                onChange={(event) =>
                                    setCloneVersionDialog((prev) => ({ ...prev, majorVersion: event.target.value }))
                                }
                                required
                                type="number"
                                fullWidth
                            />
                            <TextField
                                label="Minor Version"
                                value={cloneVersionDialog.minorVersion}
                                onChange={(event) =>
                                    setCloneVersionDialog((prev) => ({ ...prev, minorVersion: event.target.value }))
                                }
                                required
                                type="number"
                                fullWidth
                            />
                        </Stack>
                        <TextField
                            label="Description"
                            value={cloneVersionDialog.description}
                            onChange={(event) =>
                                setCloneVersionDialog((prev) => ({ ...prev, description: event.target.value }))
                            }
                            multiline
                            minRows={2}
                        />
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <TextField
                                label="Effective Date"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={cloneVersionDialog.effectiveDate}
                                onChange={(event) =>
                                    setCloneVersionDialog((prev) => ({ ...prev, effectiveDate: event.target.value }))
                                }
                                fullWidth
                            />
                            <TextField
                                label="Expiration Date"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={cloneVersionDialog.expirationDate}
                                onChange={(event) =>
                                    setCloneVersionDialog((prev) => ({ ...prev, expirationDate: event.target.value }))
                                }
                                fullWidth
                            />
                        </Stack>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={cloneVersionDialog.active}
                                    onChange={(event) =>
                                        setCloneVersionDialog((prev) => ({ ...prev, active: event.target.checked }))
                                    }
                                />
                            }
                            label="Active"
                        />
                        {cloneVersionDialog.error && <Alert severity="error">{cloneVersionDialog.error}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCloneVersionDialog} disabled={cloneVersionDialog.submitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCloneVersionSubmit}
                        disabled={cloneVersionDialog.submitting}
                    >
                        {cloneVersionDialog.submitting ? "Cloning..." : "Clone Version"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RubricsPanel;
